import { meKeys } from '../lib/meQueryKeys.ts';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { companyApi } from '@l2u/ui/lib/api.ts';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { CompanyStatusBadge } from '@l2u/ui/components/StatusBadge.tsx';
import { docActionCount, uploadDocStats } from '../lib/docStats.ts';
import { vendorDisplayStatus } from '@l2u/ui/lib/companyStatus.ts';
import { complianceDocName } from '@l2u/ui/lib/complianceDocName.ts';
import { documentAwaitingVendor } from '@l2u/ui/lib/documentsTab.ts';
import { documentsNeedingAttention } from '../lib/documentsAttention.ts';
import {
  expiryCountdownLabel,
  expiryState,
  formatDateOnly,
  type ExpiryState,
} from '@l2u/ui/lib/expiry.ts';
import { daysUntilExpirySydney } from '@l2u/shared/sydneyDates';
import type {
  CompanyDocument,
  VendorCompany,
  CompanyStatus,
  Submission,
  SubmissionFile,
  Questionnaire,
  Question,
} from '@l2u/ui/lib/types.ts';
import { computeProgress } from '@l2u/ui/lib/visibility.ts';
import {
  questionnaireActionItems,
  questionnaireSubmitted,
  actionReasonSummary,
  type VendorActionItem,
} from '@l2u/ui/lib/vendorActions.ts';
import { isQuestionnaireFullyAnswered } from '../lib/questionnaireStatus.ts';

// Statuses that mean the vendor is not (or not yet) fully compliant and should
// be directed to the questionnaire to take action (task 13).
const NON_COMPLIANT_STATUSES = new Set<CompanyStatus>([
  'invited',
  'in_progress',
  'review',
  'changes',
  'expired',
]);

// Left-border + accent text per expiry state for the Document Expiries rows.
// 'none' never occurs here (rows are filtered to files WITH an expiry date).
const EXPIRY_ROW: Record<ExpiryState, { border: string; text: string }> = {
  expired: { border: '#EF4444', text: 'text-red-600' },
  expiring: { border: '#F59E0B', text: 'text-amber-600' },
  ok: { border: '#10B981', text: 'text-emerald-600' },
  none: { border: '#10B981', text: 'text-emerald-600' },
};

export function CompanyHome() {
  const meQuery = useQuery({
    queryKey: meKeys.company(),
    queryFn: () => companyApi.get<{ company: VendorCompany }>('/api/me'),
  });
  const submissionsQuery = useQuery({
    queryKey: meKeys.submissions(),
    queryFn: () => companyApi.get<{ submissions: Submission[] }>('/api/me/submissions'),
  });
  const questionsQuery = useQuery({
    queryKey: meKeys.questions(),
    queryFn: () => companyApi.get<{ questions: Question[] }>('/api/me/questions'),
  });
  const questionnairesQuery = useQuery({
    queryKey: meKeys.questionnaires(),
    queryFn: () => companyApi.get<{ questionnaires: Questionnaire[] }>('/api/me/questionnaires'),
  });
  // Standard documents: the document rows below must match the Documents nav
  // badge, which counts an adoption only while its standard document exists.
  const documentsQuery = useQuery({
    queryKey: meKeys.documents(),
    queryFn: () => companyApi.get<{ documents: CompanyDocument[] }>('/api/me/documents'),
  });

  if (
    meQuery.isLoading ||
    submissionsQuery.isLoading ||
    questionsQuery.isLoading ||
    questionnairesQuery.isLoading ||
    documentsQuery.isLoading
  )
    return <CenteredSpinner />;
  const company = meQuery.data?.company;
  if (!company) return null;

  const submissions = submissionsQuery.data?.submissions ?? [];
  const allQuestions = questionsQuery.data?.questions ?? [];
  const questionnaires = questionnairesQuery.data?.questionnaires ?? [];
  const subByQ = new Map(submissions.map((s) => [s.question_id, s]));

  type ActionRow =
    | { kind: 'action'; id: string; name: string; count: number; summary: string }
    | { kind: 'continue'; id: string; name: string; progress: number; ready: boolean }
    // id is the QUESTION id: the row deep-links to /documents?question=<id>.
    | { kind: 'document'; id: string; name: string; reason: 'reply' | 'expired' };

  const actionRows: ActionRow[] = [];
  let totalActionItems = 0;
  // Visibility is computed per questionnaire, so union the sets to get one
  // portal-wide view for docActionCount (task 4). A question outside every
  // applicable questionnaire never enters the union, which matches how
  // /api/me/questions is already scoped.
  const allVisibleIds = new Set<string>();
  for (const qn of questionnaires) {
    const scoped = allQuestions.filter((q) => q.questionnaire_id === qn.id);
    if (scoped.length === 0) continue;
    const breakdown = computeProgress(scoped, submissions);
    for (const id of breakdown.visibleIds) allVisibleIds.add(id);
    // Per-answer submitted_at, the same gate the questionnaire page and picker
    // use. Not the company status: a vendor already in review/compliant who is
    // assigned another questionnaire has not submitted THAT one.
    const submitted = questionnaireSubmitted(scoped, subByQ);
    const visible = scoped.filter((q) => breakdown.visibleIds.has(q.id));
    const items: VendorActionItem[] = questionnaireActionItems(
      visible,
      subByQ,
      breakdown.completionByQuestion,
    );
    if (items.length > 0) {
      totalActionItems += items.length;
      actionRows.push({
        kind: 'action',
        id: qn.id,
        name: qn.name,
        count: items.length,
        summary: actionReasonSummary(items),
      });
    } else if (!submitted) {
      // Answering everything is not submitting — the row stays until Submit,
      // reading "Ready to submit" once nothing is left to answer.
      actionRows.push({
        kind: 'continue',
        id: qn.id,
        name: qn.name,
        progress: breakdown.progress,
        ready: isQuestionnaireFullyAnswered(scoped, breakdown),
      });
    }
  }

  const now = new Date();
  const questionById = new Map(allQuestions.map((q) => [q.id, q]));
  // Documents-tab documents needing attention: the SAME helper and inputs as the
  // Documents nav badge (CompanyShell), so these rows and the badge agree.
  const docById = new Map((documentsQuery.data?.documents ?? []).map((d) => [d.id, d]));
  const subById = new Map(submissions.map((s) => [s.id, s]));
  for (const subId of documentsNeedingAttention(submissions, questionById, docById, now)) {
    const sub = subById.get(subId);
    const question = sub && questionById.get(sub.question_id);
    if (!sub || !question) continue;
    actionRows.push({
      kind: 'document',
      id: sub.question_id,
      name: complianceDocName(question),
      reason: documentAwaitingVendor(sub) ? 'reply' : 'expired',
    });
  }

  // Document rows live on the Documents page, so a card of only document rows
  // sends "Review all" there; any questionnaire row keeps the questionnaire.
  const reviewAllTo = actionRows.every((row) => row.kind === 'document')
    ? '/documents'
    : '/questionnaire';

  const stats = uploadDocStats(submissions, now);
  // Task 14: attach question_id to each file so rows can deep-link to the
  // Documents page with ?question=<id>.
  //
  // Task 15: only COMPLIANT documents appear here. Filtering on the STORED
  // status is deliberate — effectiveDocStatus downgrades an approved-but-lapsed
  // document to "Not Compliant", and filtering on that would hide exactly the
  // rows this panel exists to surface.
  const expiryFiles = submissions
    .filter((s) => s.status === 'approved')
    .flatMap((s) => s.submission_files.map((f) => ({ ...f, question_id: s.question_id })))
    .filter(
      (f): f is SubmissionFile & { expires_at: string; question_id: string } =>
        !f.superseded_at && !!f.expires_at,
    )
    .sort((a, b) => a.expires_at.localeCompare(b.expires_at));

  // Task 4: outstanding documents take the headline; expiry health is the
  // fallback when nothing is owed.
  const docActions = docActionCount(allQuestions, submissions, allVisibleIds);
  const docsCard =
    docActions > 0
      ? { value: `${docActions} need action`, cls: 'text-red-600' }
      : stats.expired > 0
        ? { value: `${stats.expired} expired`, cls: 'text-red-600' }
        : stats.expiring > 0
          ? { value: `${stats.expiring} expiring`, cls: 'text-amber-600' }
          : { value: 'All current', cls: 'text-emerald-600' };

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-semibold text-slate-900">My Dashboard</h1>

      {/* Welcome banner */}
      <div
        className="rounded-xl px-5 py-4 text-white"
        style={{ background: 'var(--brand-primary)' }}
      >
        <div className="text-xs opacity-80">Welcome,</div>
        <div className="text-xl font-bold">{company.company_name}</div>
        {company.stations && company.stations.length > 0 ? (
          <div className="mt-0.5 text-xs opacity-80">{company.stations.join(', ')}</div>
        ) : null}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {/* Task 13: non-compliant status badge links to the questionnaire */}
        <StatCard label="Status">
          {/* Task 9: the badge reads "Action Required" whenever the vendor owes
              items, whatever the stored status. The link wrapper still keys off
              the real status — it decides whether the questionnaire is somewhere
              worth sending them, which is a different question. */}
          {NON_COMPLIANT_STATUSES.has(company.status) ? (
            <Link to="/questionnaire" className="inline-block">
              <CompanyStatusBadge
                status={vendorDisplayStatus(company.status, totalActionItems)}
                audience="vendor"
              />
            </Link>
          ) : (
            <CompanyStatusBadge
              status={vendorDisplayStatus(company.status, totalActionItems)}
              audience="vendor"
            />
          )}
        </StatCard>
        <StatCard label="Documents">
          <span className={`text-sm font-bold ${docsCard.cls}`}>{docsCard.value}</span>
        </StatCard>
        <StatCard label="Questionnaire">
          {totalActionItems > 0 ? (
            <Link to="/questionnaire" className="text-sm font-bold text-red-600 hover:underline">
              {totalActionItems} need action
            </Link>
          ) : (
            <span className="text-sm font-bold" style={{ color: 'var(--brand-primary)' }}>
              {company.progress}% complete
            </span>
          )}
        </StatCard>
      </div>

      {/* Needs your action — design imported from Claude Design
          (NeedsYourAction.dc.html): warm amber card, orange count chips, a
          per-row category/action summary and an "N items" status pill. */}
      {actionRows.length > 0 && (
        <section
          className="overflow-hidden rounded-xl border border-[#F3D592] bg-[#FFFAF0]"
          style={{
            boxShadow: '0 1px 2px rgba(120,80,10,0.05), 0 6px 16px rgba(120,80,10,0.08)',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 pb-[18px] pt-[22px]">
            <h2 className="flex items-center gap-3">
              <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#FF6600] text-xs font-semibold text-white">
                {actionRows.length}
              </span>
              <span className="text-base font-semibold text-[#09161E]">Needs your action</span>
            </h2>
            <Link
              to={reviewAllTo}
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-[#E0560C] hover:underline"
            >
              Review all <span className="text-[15px]">→</span>
            </Link>
          </div>

          <div className="mx-6 h-px bg-[#F1DEAF]" />

          {/* Items */}
          <div className="px-3 pb-3.5 pt-1.5">
            {actionRows.map((row) => {
              const { badge, subtitle, pill, to } =
                row.kind === 'action'
                  ? {
                      badge: String(row.count),
                      subtitle: row.summary,
                      pill: `${row.count} item${row.count === 1 ? '' : 's'}`,
                      to: `/questionnaire?type=${row.id}&action=1`,
                    }
                  : row.kind === 'continue' && row.ready
                    ? {
                        badge: '✓',
                        subtitle: 'All questions answered — submit to finish',
                        pill: 'Ready to submit',
                        to: `/questionnaire?type=${row.id}`,
                      }
                    : row.kind === 'continue'
                      ? {
                          badge: `${row.progress}%`,
                          subtitle: 'Continue your questionnaire',
                          pill: `${row.progress}% complete`,
                          to: `/questionnaire?type=${row.id}`,
                        }
                      : {
                          badge: '!',
                          subtitle:
                            row.reason === 'reply'
                              ? 'Reply to your reviewer'
                              : 'Expired — upload a renewal',
                          pill: 'Document',
                          to: `/documents?question=${row.id}`,
                        };
              return (
                <Link
                  // Questionnaire and question ids share one list: key by kind too.
                  key={`${row.kind}:${row.id}`}
                  to={to}
                  className="flex items-center gap-4 rounded-[10px] px-3 py-4 transition hover:bg-[#FCEFCF]"
                >
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-[10px] bg-[#FF6600] text-white">
                    <span
                      className={`font-semibold leading-none ${
                        row.kind === 'continue' ? 'text-sm' : 'text-base'
                      }`}
                    >
                      {badge}
                    </span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 text-sm font-semibold text-[#09161E]">{row.name}</div>
                    <div className="text-sm text-[#667085]">{subtitle}</div>
                  </div>
                  <div className="flex flex-none items-center gap-3.5">
                    <span className="whitespace-nowrap rounded-full border border-[#F1DEAF] bg-white px-2.5 py-[5px] text-[11px] font-medium uppercase tracking-[0.04em] text-[#8A5A06]">
                      {pill}
                    </span>
                    <span className="text-lg leading-none text-[#98A2B3]">›</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Document expiries */}
      {expiryFiles.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Document Expiries</h2>
            <Link
              to="/documents"
              className="text-xs font-medium hover:underline"
              style={{ color: 'var(--brand-primary)' }}
            >
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {expiryFiles.map((f) => {
              const state = expiryState(f, now);
              const row = EXPIRY_ROW[state];
              const days = daysUntilExpirySydney(f.expires_at, now) ?? 0;
              // Task 16: name the compliance document this file answers, so the
              // vendor knows WHAT is expiring, not just which file.
              const question = questionById.get(f.question_id);
              // Task 14: deep-link to the specific question on the Documents page.
              const docsHref = f.question_id
                ? `/documents?question=${f.question_id}`
                : '/documents';
              return (
                <Link
                  key={f.id}
                  to={docsHref}
                  className="block rounded-md border-l-4 bg-slate-50 px-3 py-2 transition hover:brightness-95"
                  style={{ borderLeftColor: row.border }}
                >
                  {question && (
                    <div className="truncate text-[11px] font-medium text-slate-500">
                      {complianceDocName(question)}
                    </div>
                  )}
                  <div
                    className="truncate text-xs font-medium"
                    style={{ color: 'var(--brand-primary)' }}
                  >
                    {f.file_name}
                  </div>
                  <div className="text-sm font-semibold text-slate-900">
                    {formatDateOnly(f.expires_at)}
                  </div>
                  <div className={`text-xs ${row.text}`}>{expiryCountdownLabel(state, days)}</div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Quick info */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-2 text-sm font-semibold text-slate-900">Quick Info</h2>
        <dl className="divide-y divide-slate-100">
          <InfoRow
            label="Contractor Type"
            value={
              company.contractor_types && company.contractor_types.length > 0
                ? company.contractor_types.join(', ')
                : null
            }
          />
          <InfoRow label="Vendor Number" value={company.vendor_number} />
          <InfoRow
            label="Station"
            value={
              company.stations && company.stations.length > 0 ? company.stations.join(', ') : null
            }
          />
          <InfoRow
            label="Cluster Region"
            value={
              company.cluster_regions && company.cluster_regions.length > 0
                ? company.cluster_regions.join(', ')
                : null
            }
          />
        </dl>
      </section>
    </div>
  );
}

function StatCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-sm font-semibold text-slate-900">{value ?? '—'}</dd>
    </div>
  );
}
